// Story 4.5 — /account/profile page.
//
// FIRST SPA surface composing handle (POST /api/v1/account/handle) +
// bio + profile_visibility (PATCH /api/v1/account/preferences) +
// view-link anchor for the public Astro page at /u/{handle}. The
// read-side counterpart to Story 4.4's writer-side highlight/favourite
// toggles.
//
// 4 sections:
//   1. Handle — text input + Save. Confirmation modal on CHANGE (not
//      first claim) warning about the 90-day redirect.
//   2. Public bio — textarea + Save + 500-char counter.
//   3. Profile visibility — <Switch> (Public ↔ Private). Second
//      non-share-detail Switch consumer in apps/web after Story 4.4.
//   4. View link — anchor to the Astro public profile page (opens in
//      new tab). Disabled with a hint when profile is private.
//
// Layout shell mirrors Story 3.7 AccountPreferencesPage exactly so
// account-area pages cluster visually.

import { useEffect, useState, type ChangeEvent } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Switch,
  Textarea,
} from "@greenhold/components";

import { useAccountMe } from "../features/admin/useAccountMe";
import { useChangeHandle } from "../features/account/useChangeHandle";
import { publicCollectionUrl } from "../lib/publicSiteUrls";
import {
  useUpdatePreferences,
  type ProfileVisibilityValue,
} from "../features/preferences/useUpdatePreferences";

const HANDLE_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const HANDLE_MAX_LEN = 60;
const PUBLIC_BIO_MAX_LEN = 500;

/// Use the Astro public-site origin in production; fall back to
/// `window.location.origin` for local dev where the Astro site is
/// often served from a different port (4321) than the SPA (5173). The
/// Story 10.6 — the /u/ collection page is served by THIS SPA host, so its
/// URL resolves to the SPA origin via `publicCollectionUrl` (NOT the Astro
/// storefront's VITE_PUBLIC_SITE_ORIGIN).

function isValidHandleFormat(value: string): boolean {
  if (value.length < 1 || value.length > HANDLE_MAX_LEN) return false;
  return HANDLE_REGEX.test(value);
}

export function AccountProfilePage() {
  const accountMe = useAccountMe();
  const changeHandleMutation = useChangeHandle();
  const updatePreferencesMutation = useUpdatePreferences();

  const currentHandle = accountMe.data?.data.handle ?? null;
  const currentBio = accountMe.data?.data.public_bio ?? "";
  const currentVisibility: ProfileVisibilityValue =
    accountMe.data?.data.profile_visibility ?? "public";

  const [handleInput, setHandleInput] = useState("");
  const [handleError, setHandleError] = useState<string | null>(null);
  const [bioInput, setBioInput] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Seed the inputs from AccountMe once it loads.
  useEffect(() => {
    if (accountMe.data) {
      setHandleInput(accountMe.data.data.handle ?? "");
      setBioInput(accountMe.data.data.public_bio ?? "");
    }
  }, [accountMe.data]);

  if (accountMe.isLoading) {
    return (
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
        <p className="font-body text-sm opacity-80">Loading…</p>
      </main>
    );
  }

  if (accountMe.isError) {
    return (
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
        <p className="font-body text-sm opacity-80">
          Sign in to manage your public profile.{" "}
          <Link to="/" className="underline decoration-dotted">
            Go home
          </Link>
        </p>
      </main>
    );
  }

  function submitHandleChange(target: string) {
    changeHandleMutation.mutate(
      { handle: target },
      {
        onSuccess: () => {
          toast.success(
            currentHandle
              ? "Handle changed. Old URL redirects for 90 days."
              : "Handle claimed.",
          );
          setHandleError(null);
        },
        onError: (error) => {
          if (error.code === "HANDLE_TAKEN") {
            setHandleError("That handle is taken — try another.");
          } else if (error.code === "VALIDATION_FAILED") {
            setHandleError(error.message);
          } else {
            toast.error(error.message);
          }
        },
      },
    );
  }

  function handleSaveHandleClick() {
    const trimmed = handleInput.trim();
    setHandleError(null);
    if (!isValidHandleFormat(trimmed)) {
      setHandleError(
        "Handle must be 1–60 chars, lowercase letters/digits, hyphens between (no leading/trailing/double hyphens).",
      );
      return;
    }
    if (trimmed === currentHandle) {
      toast("No change.");
      return;
    }
    // FIRST claim → straight save. CHANGE → modal confirmation first.
    if (currentHandle === null) {
      submitHandleChange(trimmed);
    } else {
      setConfirmModalOpen(true);
    }
  }

  function handleConfirmChange() {
    setConfirmModalOpen(false);
    submitHandleChange(handleInput.trim());
  }

  function handleSaveBioClick() {
    // Empty input clears the bio (matches backend v1 contract).
    const trimmed = bioInput.trim();
    if (trimmed.length > PUBLIC_BIO_MAX_LEN) {
      toast.error(`Bio must be ${PUBLIC_BIO_MAX_LEN} characters or fewer.`);
      return;
    }
    if ((trimmed || null) === (currentBio || null)) {
      toast("No change.");
      return;
    }
    updatePreferencesMutation.mutate(
      { public_bio: trimmed },
      {
        onSuccess: () => toast.success("Bio saved."),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  function handleVisibilityChange(next: boolean) {
    const value: ProfileVisibilityValue = next ? "public" : "private";
    if (value === currentVisibility) return;
    updatePreferencesMutation.mutate(
      { profile_visibility: value },
      {
        onSuccess: () =>
          toast.success(
            value === "public"
              ? "Profile is now public."
              : "Profile is now private.",
          ),
        onError: (error) => toast.error(error.message),
      },
    );
  }

  function handleBioChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setBioInput(event.target.value);
  }

  const bioChars = bioInput.trim().length;
  const bioOverLimit = bioChars > PUBLIC_BIO_MAX_LEN;
  const publicProfileUrl = currentHandle
    ? publicCollectionUrl(currentHandle)
    : null;

  // Story 10.5 — the SPA's single clipboard idiom: navigator.share when
  // available, clipboard.writeText fallback, toast either way.
  async function handleCopyProfileUrl() {
    if (!publicProfileUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "My Greenhold collection", url: publicProfileUrl });
        return;
      } catch {
        // user cancelled; fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(publicProfileUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — try again from your browser.");
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 p-6 text-brand-paper-100">
      <header>
        <h1 className="font-heading text-4xl font-bold">Public profile</h1>
        <p className="font-body text-sm opacity-80 mt-2 leading-relaxed">
          Set your handle, write a short bio, and decide whether your
          profile is visible to anyone on the web.
        </p>
      </header>

      {/* Section 1 — Handle */}
      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Handle</h2>
        <p className="font-body text-xs opacity-70">
          Your handle is the URL of your public profile:{" "}
          <span className="font-mono">/u/{currentHandle ?? "your-handle"}</span>
          . Lowercase letters, digits, and hyphens — between 1 and 60
          characters.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            type="text"
            inputMode="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={handleInput}
            onChange={(e) => setHandleInput(e.target.value)}
            placeholder="your-handle"
            aria-label="Handle"
            disabled={changeHandleMutation.isPending}
            maxLength={HANDLE_MAX_LEN}
            className="font-mono"
          />
          <Button
            onClick={handleSaveHandleClick}
            disabled={changeHandleMutation.isPending || handleInput.trim() === ""}
          >
            {currentHandle ? "Save handle" : "Claim handle"}
          </Button>
        </div>
        {handleError && (
          <p className="font-body text-sm text-state-error-fg">{handleError}</p>
        )}
      </section>

      {/* Section 2 — Public bio */}
      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Public bio</h2>
        <p className="font-body text-xs opacity-70">
          A short blurb shown on your public profile. Markdown is not
          rendered — plain text only.
        </p>
        <Textarea
          value={bioInput}
          onChange={handleBioChange}
          placeholder="Casting analyst by day, dossier collector by night."
          aria-label="Public bio"
          rows={4}
          disabled={updatePreferencesMutation.isPending}
        />
        <div className="flex items-center justify-between gap-2">
          <span
            className={
              bioOverLimit
                ? "font-body text-xs text-state-error-fg"
                : "font-body text-xs opacity-70"
            }
          >
            {bioChars}/{PUBLIC_BIO_MAX_LEN}
          </span>
          <Button
            onClick={handleSaveBioClick}
            disabled={updatePreferencesMutation.isPending || bioOverLimit}
          >
            Save bio
          </Button>
        </div>
      </section>

      {/* Section 3 — Profile visibility */}
      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-heading text-lg font-semibold">Visibility</h2>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col">
            <label
              htmlFor="profile-visibility"
              className="font-body text-sm font-medium cursor-pointer"
            >
              Public profile
            </label>
            <span className="font-body text-xs opacity-70 mt-1">
              Public profiles appear at <span className="font-mono">/u/{currentHandle ?? "your-handle"}</span>.
              Private profiles return 404 to everyone except you.
            </span>
          </div>
          <Switch
            id="profile-visibility"
            checked={currentVisibility === "public"}
            disabled={updatePreferencesMutation.isPending}
            onCheckedChange={handleVisibilityChange}
          />
        </div>
      </section>

      {/* Section 4 — View + copy link */}
      <section className="rounded-lg border border-border bg-card/40 p-6 space-y-3">
        {publicProfileUrl ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm">{publicProfileUrl}</span>
              <Button
                variant="secondary"
                onClick={handleCopyProfileUrl}
                aria-label="Copy public profile link"
              >
                Copy link
              </Button>
              {currentVisibility === "public" && (
                <a
                  href={publicProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-sm font-semibold underline decoration-dotted"
                >
                  View →
                </a>
              )}
            </div>
            {currentVisibility !== "public" && (
              <p className="font-body text-sm opacity-70">
                Your profile is private — this link returns 404 to everyone
                except you until you make it public above. Individual items you
                publish still open via their own permalinks.
              </p>
            )}
          </>
        ) : (
          <p className="font-body text-sm opacity-70">
            Publish an item — or claim a handle above — to get a shareable
            public profile URL.
          </p>
        )}
      </section>

      <AlertDialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change your handle?</AlertDialogTitle>
            <AlertDialogDescription>
              Your old handle{" "}
              <span className="font-mono font-semibold">/u/{currentHandle}</span>{" "}
              will redirect to your new one for 90 days. After that, links
              to your old handle will return 404.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmChange}>
              Change handle
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
